/**
 * useCanvasEditorOverlay — JointJS -> Canvas migration (Phase 1 + Phase 2).
 *
 * When the CANVAS_EDITOR flag is ON, mounts the viewer's Canvas renderer on top
 * of the editor's (now frozen) JointJS paper, bound to the SAME live dia.Graph.
 *   - Phase 1: render the real editor graph on Canvas (SVG stays frozen/hidden).
 *   - Phase 2: click selection (single / ctrl|shift multi / blank-clear) wired to
 *     the editorStore, so the right panel + keyboard delete react to it, plus the
 *     Canvas selection highlight. Graph mutations (delete, etc.) update live via
 *     bindToGraph.
 *
 * When the flag is OFF this hook is a no-op: the editor behaves exactly as before.
 */

import { useEffect, useRef, RefObject } from 'react'
import { dia } from '@joint/core'
import { CanvasRenderer } from '../../../map-editor-features/viewer/canvas/CanvasRenderer'
import { setActiveCanvasRenderer, getActiveCanvasRenderer } from '../../../map-editor-features/viewer/canvas/activeRenderer'
import { getFlag } from '../../../config/featureFlags'
import { useEditorStore } from '../../../map-editor-shared/store/editorStore'
import { useObjectTypeStore, ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useZoneDrawingStore } from '../../../map-editor-shared/store/zoneDrawingStore'
import { ZONE_DEFAULT_COLOR } from '../../../map-editor-shared/api/zoneApi'
import { ElementId } from '../../../map-editor-shared/types/branded'
import { createSingleClickObject, finishLineObject, finishPolygonObject } from '../lib/objectCreationCore'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'

import type { CanvasRenderer as CanvasRendererType } from '../../../map-editor-features/viewer/canvas/CanvasRenderer'

// 현재 마운트된 오버레이 캔버스(플래그 ON 에서 실제로 보이는 화면).
// 저장 썸네일 캡처(useSaveProject)가 얼려진 SVG 대신 이 캔버스를 쓴다.
let activeOverlayCanvas: HTMLCanvasElement | null = null
export const getActiveOverlayCanvas = (): HTMLCanvasElement | null => activeOverlayCanvas

const CLICK_MOVE_THRESHOLD = 4 // px; movement below this counts as a click, not a pan

/** Relationship edit mode inputs for the canvas overlay: when editing, clicks
 *  on a target id link it (via onTargetClick) instead of selecting. */
export interface CanvasRelationEdit {
  editing: boolean
  targetIds: string[]
  onTargetClick: (targetId: string) => void
}

export function useCanvasEditorOverlay(
  containerRef: RefObject<HTMLDivElement | null>,
  graph: dia.Graph | null,
  paper: dia.Paper | null,
  currentTool?: string | null,
  selectedType?: ObjectType | null,
  onObjectCreated?: () => void,
  relationTypes?: Record<string, TemplateRelationType>,
  relationEdit?: CanvasRelationEdit
): void {
  // Keep live inputs in refs so event handlers read the latest values without
  // re-running (and tearing down) the main setup effect when they change.
  const toolRef = useRef<string | null | undefined>(currentTool)
  const selectedTypeRef = useRef<ObjectType | null | undefined>(selectedType)
  const onObjectCreatedRef = useRef<(() => void) | undefined>(onObjectCreated)
  const relationTypesRef = useRef<Record<string, TemplateRelationType> | undefined>(relationTypes)
  const relationEditRef = useRef<CanvasRelationEdit | undefined>(relationEdit)
  const rendererRef = useRef<CanvasRendererType | null>(null)
  useEffect(() => {
    toolRef.current = currentTool
    selectedTypeRef.current = selectedType
    onObjectCreatedRef.current = onObjectCreated
    relationTypesRef.current = relationTypes
    relationEditRef.current = relationEdit
    // Relationship display is selection-driven: providing relationTypes lets the
    // renderer draw a selected element's relationships (matches editor overlay).
    if (rendererRef.current && relationTypes) {
      rendererRef.current.setRelationTypes(relationTypes)
    }
    // Edit-mode target outlines (green): mirror the paper highlight behavior.
    if (rendererRef.current) {
      rendererRef.current.setEditTargetHighlights(
        relationEdit?.editing ? relationEdit.targetIds : []
      )
    }
  }, [currentTool, selectedType, onObjectCreated, relationTypes, relationEdit])

  useEffect(() => {
    if (!getFlag('CANVAS_EDITOR')) return
    const container = containerRef.current
    if (!container || !graph || !paper) return

    // Hide the (frozen) SVG paint but keep it laid out — coords stay valid for
    // any hook that still reads paper geometry.
    const svg = paper.el as unknown as HTMLElement
    const prevVisibility = svg.style.visibility
    svg.style.visibility = 'hidden'

    // Canvas overlay on top of the hidden SVG.
    const canvasEl = document.createElement('canvas')
    canvasEl.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;z-index:2;cursor:grab'
    container.appendChild(canvasEl)

    const renderer = new CanvasRenderer(canvasEl, { enableCulling: true })
    rendererRef.current = renderer
    activeOverlayCanvas = canvasEl
    setActiveCanvasRenderer(renderer)
    // Match the editor's default look: geometry only (no CCTV/parking names or
    // always-on link fans, which the viewer renderer shows by default).
    renderer.setLabelVisibility({ cctv: false, parking: false, links: false })
    // Enable selection-driven relationship lines (colored per relation type).
    if (relationTypesRef.current) renderer.setRelationTypes(relationTypesRef.current)

    const applySize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      if (w > 0 && h > 0) {
        canvasEl.width = w
        canvasEl.height = h
        renderer.resize(w, h)
      }
    }

    // The editor populates the graph asynchronously (often with silent addCells,
    // which fire no events). Wait until it has cells, then bind live + fit once.
    // bindToGraph keeps the Canvas in sync with later mutations (delete, move).
    // No poll give-up: on an empty floor the graph stays empty until the user
    // finishes a DXF/CSV import minutes later, and the bind must still happen.
    let bound = false
    const tryBind = () => {
      if (bound) return
      if (graph.getCells().length === 0) return
      bound = true
      applySize()
      renderer.bindToGraph(graph)
      renderer.fitToContent()
      window.clearInterval(pollTimer)
      graph.off('add reset', tryBind)
    }
    const initialTimer = window.setTimeout(tryBind, 100)
    const pollTimer = window.setInterval(tryBind, 300)
    graph.on('add reset', tryBind)
    const resizeObserver = new ResizeObserver(applySize)
    resizeObserver.observe(container)

    // ---- Multi-click drawing (line / polygon) preview ----
    const SVG_NS = 'http://www.w3.org/2000/svg'
    let drawPoints: { x: number; y: number }[] = []
    let drawPreview: SVGSVGElement | null = null
    const updatePreview = () => {
      if (!drawPreview) {
        drawPreview = document.createElementNS(SVG_NS, 'svg')
        drawPreview.style.cssText =
          'position:absolute;top:0;left:0;width:100%;height:100%;z-index:3;pointer-events:none;overflow:visible'
        const pl = document.createElementNS(SVG_NS, 'polyline')
        pl.setAttribute('fill', 'none')
        pl.setAttribute('stroke', '#3b82f6')
        pl.setAttribute('stroke-width', '2')
        pl.setAttribute('stroke-dasharray', '4 3')
        drawPreview.appendChild(pl)
        container.appendChild(drawPreview)
      }
      const pl = drawPreview.firstChild as SVGPolylineElement
      pl.setAttribute(
        'points',
        drawPoints.map((p) => {
          const s = renderer.worldToScreen(p.x, p.y)
          return `${s.x},${s.y}`
        }).join(' ')
      )
    }
    const clearDrawing = () => {
      if (drawPreview) {
        drawPreview.remove()
        drawPreview = null
      }
      drawPoints = []
    }

    // ---- Zone polygon drawing preview (zoneDrawingStore-driven) ----
    // Zone 드로잉은 JointJS paper 이벤트(useZonePolygonDrawing)로 만들어졌지만
    // CANVAS_EDITOR 모드에선 캔버스가 모든 포인터 이벤트를 소비하므로 여기서
    // 점 추가/미리보기를 직접 처리한다. 완료/취소는 기존 ZoneDrawingHud 그대로.
    let zonePreview: SVGSVGElement | null = null
    const updateZonePreview = () => {
      const { mode, points, hoverPoint } = useZoneDrawingStore.getState()
      canvasEl.style.cursor = mode === 'drawing' ? 'crosshair' : canvasEl.style.cursor
      if (mode !== 'drawing' || points.length === 0) {
        if (zonePreview) {
          zonePreview.remove()
          zonePreview = null
        }
        return
      }
      if (!zonePreview) {
        zonePreview = document.createElementNS(SVG_NS, 'svg')
        zonePreview.style.cssText =
          'position:absolute;top:0;left:0;width:100%;height:100%;z-index:3;pointer-events:none;overflow:visible'
        const pl = document.createElementNS(SVG_NS, 'polyline')
        pl.setAttribute('fill', ZONE_DEFAULT_COLOR)
        pl.setAttribute('fill-opacity', '0.15')
        pl.setAttribute('stroke', ZONE_DEFAULT_COLOR)
        pl.setAttribute('stroke-width', '2')
        pl.setAttribute('stroke-dasharray', '4 3')
        zonePreview.appendChild(pl)
        container.appendChild(zonePreview)
      }
      const pl = zonePreview.firstChild as SVGPolylineElement
      const pts = hoverPoint ? [...points, hoverPoint] : points
      pl.setAttribute(
        'points',
        pts.map((p) => {
          const s = renderer.worldToScreen(p.x, p.y)
          return `${s.x},${s.y}`
        }).join(' ')
      )
    }
    const unsubscribeZone = useZoneDrawingStore.subscribe(updateZonePreview)

    // ---- Pan / element-drag / click selection ----
    let pointerDown = false
    let moved = false
    let lastX = 0
    let lastY = 0
    let dragMode: 'pan' | 'lasso' = 'pan'
    let lassoStartX = 0
    let lassoStartY = 0
    let marquee: HTMLDivElement | null = null
    const removeMarquee = () => {
      if (marquee) {
        marquee.remove()
        marquee = null
      }
    }
    const onDown = (e: MouseEvent) => {
      pointerDown = true
      moved = false
      lastX = e.clientX
      lastY = e.clientY
      // Creation mode: drag pans, click (onUp) places. Skip select/move/lasso so
      // clicking to place doesn't accidentally grab the element under the cursor.
      if (selectedTypeRef.current) {
        dragMode = 'pan'
        canvasEl.style.cursor = 'grabbing'
        return
      }
      // Zone 드로잉 모드: 드래그는 팬, 클릭(onUp)은 점 추가 — 선택/이동/라쏘 스킵.
      if (useZoneDrawingStore.getState().mode === 'drawing') {
        dragMode = 'pan'
        canvasEl.style.cursor = 'grabbing'
        return
      }
      const rect = canvasEl.getBoundingClientRect()
      const localX = e.clientX - rect.left
      const localY = e.clientY - rect.top
      const hitId = renderer.hitTest(localX, localY)
      const marqueeTool = toolRef.current === 'move' || toolRef.current === 'select'
      const reDown = relationEditRef.current
      if (reDown?.editing && hitId && reDown.targetIds.includes(hitId)) {
        // Relationship edit mode, pressing a linkable target: don't select or
        // start a move (that would re-render the sidebar and reset edit state
        // before onUp can link). Drag still pans; the click links in onUp.
        dragMode = 'pan'
      } else if (marqueeTool) {
        // Marquee ("Drag Select") tool: drag always does lasso selection.
        // A plain click (no drag) still selects the element under the cursor
        // via the onUp click path.
        dragMode = 'lasso'
        lassoStartX = localX
        lassoStartY = localY
      } else {
        // 객체 위에서 드래그해도 이동하지 않는다 — 항상 팬. 클릭 선택은 onUp.
        dragMode = 'pan'
      }
      canvasEl.style.cursor = 'grabbing'
    }
    const onMove = (e: MouseEvent) => {
      // Zone 드로잉: 버튼 안 눌린 이동은 커서까지의 고스트 라인 갱신.
      if (!pointerDown && useZoneDrawingStore.getState().mode === 'drawing') {
        const rect = canvasEl.getBoundingClientRect()
        const world = renderer.screenToWorld(e.clientX - rect.left, e.clientY - rect.top)
        useZoneDrawingStore.getState().setHover({ x: world.x, y: world.y })
        return
      }
      if (!pointerDown) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      if (!moved && Math.abs(dx) + Math.abs(dy) > CLICK_MOVE_THRESHOLD) moved = true
      if (!moved) return
      if (dragMode === 'pan') {
        renderer.pan(dx, dy)
        updateZonePreview() // 팬 중에도 zone 미리보기를 화면 좌표로 재투영
      } else {
        // lasso: draw the marquee rect (screen space).
        const rect = canvasEl.getBoundingClientRect()
        const curX = e.clientX - rect.left
        const curY = e.clientY - rect.top
        if (!marquee) {
          marquee = document.createElement('div')
          marquee.style.cssText =
            'position:absolute;border:1px solid #4a90d9;background:rgba(74,144,217,.15);z-index:3;pointer-events:none'
          container.appendChild(marquee)
        }
        marquee.style.left = `${Math.min(lassoStartX, curX)}px`
        marquee.style.top = `${Math.min(lassoStartY, curY)}px`
        marquee.style.width = `${Math.abs(curX - lassoStartX)}px`
        marquee.style.height = `${Math.abs(curY - lassoStartY)}px`
      }
      lastX = e.clientX
      lastY = e.clientY
    }
    const onUp = (e: MouseEvent) => {
      if (!pointerDown) return
      pointerDown = false
      canvasEl.style.cursor = 'grab'

      if (moved && dragMode === 'lasso') {
        // Finalize marquee → select all elements intersecting the world rect.
        const rect = canvasEl.getBoundingClientRect()
        const curX = e.clientX - rect.left
        const curY = e.clientY - rect.top
        const a = renderer.screenToWorld(Math.min(lassoStartX, curX), Math.min(lassoStartY, curY))
        const b = renderer.screenToWorld(Math.max(lassoStartX, curX), Math.max(lassoStartY, curY))
        const rx0 = Math.min(a.x, b.x)
        const ry0 = Math.min(a.y, b.y)
        const rx1 = Math.max(a.x, b.x)
        const ry1 = Math.max(a.y, b.y)
        const ids: string[] = []
        for (const el of graph.getElements()) {
          const bb = el.getBBox()
          if (!(bb.x > rx1 || bb.x + bb.width < rx0 || bb.y > ry1 || bb.y + bb.height < ry0)) {
            ids.push(String(el.id))
          }
        }
        const store = useEditorStore.getState()
        const additive = e.ctrlKey || e.metaKey || e.shiftKey
        const next = additive
          ? Array.from(new Set([...(store.selection.selectedElementIds as string[]), ...ids]))
          : ids
        store.setSelectedElementIds(next.map(ElementId))
        removeMarquee()
        return
      }
      removeMarquee()
      if (moved) return // it was a pan, not a click

      const rect = canvasEl.getBoundingClientRect()
      const localX = e.clientX - rect.left
      const localY = e.clientY - rect.top
      const store = useEditorStore.getState()

      // Zone 드로잉 모드: 클릭이 다각형 점 추가 (완료는 ZoneDrawingHud 버튼).
      const zoneStore = useZoneDrawingStore.getState()
      if (zoneStore.mode === 'drawing') {
        const world = renderer.screenToWorld(localX, localY)
        zoneStore.addPoint({ x: world.x, y: world.y })
        return
      }

      // Relationship edit mode: clicking a highlighted target links it to the
      // element being edited (paper parity: element clicks beat creation).
      const re = relationEditRef.current
      if (re?.editing) {
        const hitId = renderer.hitTest(localX, localY)
        if (hitId && re.targetIds.includes(hitId)) {
          re.onTargetClick(hitId)
          return
        }
      }

      // Creation mode: a type is selected in the left panel (local selectedType)
      // → place an object at the clicked world position via the shared core.
      const type = selectedTypeRef.current
      if (type) {
        const world = renderer.screenToWorld(localX, localY)
        const floorId = useFloorStore.getState().currentFloor || ''
        const mappings = useObjectTypeStore.getState().getLayerTypeMappingsByFloorId(floorId)
        const created = createSingleClickObject(graph, type, world.x, world.y, {
          mappings,
          defaultObjectSize: store.defaultObjectSize,
        })
        if (created) {
          onObjectCreatedRef.current?.() // place-one, then deselect (paper parity)
          return
        }
        // Multi-click kind (line/polygon): accumulate a point; dblclick finishes.
        drawPoints.push({ x: world.x, y: world.y })
        updatePreview()
        return
      }

      const id = renderer.hitTest(localX, localY)
      const additive = e.ctrlKey || e.metaKey || e.shiftKey

      if (!id) {
        if (!additive) store.clearSelection()
        return
      }
      if (additive) {
        const current = store.selection.selectedElementIds as string[]
        if (current.includes(id)) store.removeSelectedElementId(ElementId(id))
        else store.addSelectedElementId(ElementId(id))
      } else {
        store.setSelectedElementId(ElementId(id))
      }
    }
    canvasEl.addEventListener('mousedown', onDown)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)

    // Zoom (wheel), zoom-to-cursor.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.1 : 0.9
      const next = Math.max(0.02, Math.min(renderer.getScale() * factor, 4))
      const rect = canvasEl.getBoundingClientRect()
      renderer.zoom(next, e.clientX - rect.left, e.clientY - rect.top)
      updateZonePreview() // 줌 후 zone 미리보기 재투영
    }
    canvasEl.addEventListener('wheel', onWheel, { passive: false })

    // Double-click finishes an in-progress multi-click line/polygon.
    const onDblClick = () => {
      const type = selectedTypeRef.current
      if (!type || drawPoints.length === 0) return
      const floorId = useFloorStore.getState().currentFloor || ''
      const ctx = {
        mappings: useObjectTypeStore.getState().getLayerTypeMappingsByFloorId(floorId),
        defaultObjectSize: useEditorStore.getState().defaultObjectSize,
      }
      if ((type.kind || 'asset') === 'line') {
        finishLineObject(graph, type, drawPoints, ctx)
      } else {
        finishPolygonObject(graph, type, drawPoints, ctx)
      }
      clearDrawing()
      onObjectCreatedRef.current?.()
    }
    canvasEl.addEventListener('dblclick', onDblClick)

    // Reflect editorStore selection on the Canvas (covers canvas clicks AND
    // external selection e.g. from the right-panel object list).
    let prevSelKey = ''
    const applySelection = (ids: readonly string[]) => {
      const key = ids.join(',')
      if (key === prevSelKey) return
      prevSelKey = key
      renderer.setSelection(ids as string[])
    }
    applySelection(useEditorStore.getState().selection.selectedElementIds as string[])
    const unsubscribe = useEditorStore.subscribe((state) =>
      applySelection(state.selection.selectedElementIds as string[])
    )

    // Dev aid (flag-on experimental path only): expose store + graph + renderer.
    ;(window as any).__editorStore = useEditorStore
    ;(window as any).__editorGraph = graph
    ;(window as any).__editorRenderer = renderer
    // eslint-disable-next-line no-console
    console.info('[CanvasEditorOverlay] Phase 2 active — Canvas render + selection bound to editorStore.')

    return () => {
      window.clearTimeout(initialTimer)
      window.clearInterval(pollTimer)
      graph.off('add reset', tryBind)
      resizeObserver.disconnect()
      canvasEl.removeEventListener('mousedown', onDown)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      canvasEl.removeEventListener('wheel', onWheel)
      canvasEl.removeEventListener('dblclick', onDblClick)
      clearDrawing()
      removeMarquee()
      unsubscribe()
      unsubscribeZone()
      if (zonePreview) {
        zonePreview.remove()
        zonePreview = null
      }
      renderer.destroy()
      rendererRef.current = null
      if (activeOverlayCanvas === canvasEl) activeOverlayCanvas = null
      if (getActiveCanvasRenderer() === renderer) setActiveCanvasRenderer(null)
      if (canvasEl.parentNode) canvasEl.parentNode.removeChild(canvasEl)
      svg.style.visibility = prevVisibility
    }
  }, [containerRef, graph, paper])
}
